import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useToggle } from "../../../../hooks/useToggle";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import { QRCodeCanvas } from "qrcode.react";
import Loading from "../../../../components/Loading/Loading";
import DashboardLayout from "../../layouts/DashboardLayout";
import DashboardTopbar from "../../components/DashboardTopbar/DashboardTopbar";
import KPICard from "../../components/shared/KPICard/KPICard";
import MetricCard from "../../components/shared/MetricCard/MetricCard";
import PerformanceChart from "../../components/shared/PerformanceChart/PerformanceChart";
import EconomicCircle from "../../components/shared/EconomicCircle/EconomicCircle";
import AssociationConfimeaShare from "../../components/shared/AssociationConfimeaShare/AssociationConfimeaShare";
import RecentOperations from "../../components/shared/RecentOperations/RecentOperations";
import useDashboardData from "../../hooks/useDashboardData";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import { formatFloatToInteger } from "../../../../utilities/formatFloatToInteger";
import localeText from "../../../../locale";
import PagamentoSeguro from "../../../images/pagamentoSeguro.png";
import paypalImg from "../../../../pages/images/paypal.png";
import masterCardImg from "../../../../pages/images/masterCard.png";
import americanExpress from "../../../../pages/images/expressoAmericano.png";
import visa from "../../../../pages/images/visa.png";
import "./ManagerDashboardHome.css";
import { formatDate } from "../../../../utilities/formatDate";
import CreateTransactionDialog from "../../components/CreateTransactionDialog/CreateTransactionDialog";
import SubmitProofDialog from "../../components/SubmitProofDialog/SubmitProofDialog";

const ManagerDashboardHome = ({ userTypeInfo, userInfo }) => {
  const history = useHistory();
  const { data, isLoading, error } = useDashboardData();
  const { currencySymbol: currency } = useCurrency(data?.walletInfo?.currency);
  const currencyCode = data?.walletInfo?.currency;

  const [isLoadingCreateTransaction, setIsLoadingCreateTransaction] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [isLoadingSubmitProof, setIsLoadingSubmitProof] = useState(false);
  const [verificationIsPending, setVerificationIsPending] = useState(false);
  const [verificationRejectionReason, setVerificationRejectionReason] = useState(null);
  const [transactionObj, setTransactionObj] = useState();

  const { showSuccessToast, showErrorToast } = useToastContext();
  const {
    value: isOpenCreateTransactionModal,
    setAsTrue: openCreateTransactionModal,
    setAsFalse: closeCreateTransactionModal,
  } = useToggle();
  const {
    value: isOpenQrCodeTransactionModal,
    setAsTrue: openQrCodeTransactionModal,
    setAsFalse: closeQrCodeTransactionModal,
  } = useToggle();
  const {
    value: isOpenSubmitProofModal,
    setAsTrue: openSubmitProofModal,
    setAsFalse: closeSubmitProofModal,
  } = useToggle();

  const physicalStoreService = new PhysicalStoreService();
  const metricContainerRef = useRef(null);
  const metricScrollRef = useRef(null);
  const economicCircleRef = useRef(null);
  const [circleHeight, setCircleHeight] = useState(null);
  const [metricScrollState, setMetricScrollState] = useState({
    needsScroll: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const METRIC_SCROLL_AMOUNT = 280;
  const METRIC_MIN_WIDTH = 1054;
  const LG_BREAKPOINT = 992;

  const updateMetricScrollState = useCallback(() => {
    const container = metricContainerRef.current;
    const scrollEl = metricScrollRef.current;
    if (!container || !scrollEl) return;
    const containerWidth = container.getBoundingClientRect().width;
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
    const hasOverflow = scrollWidth > clientWidth;
    const containerNarrow = containerWidth < METRIC_MIN_WIDTH;
    const needsScroll = hasOverflow || containerNarrow;
    const canScrollLeft = needsScroll && scrollLeft > 2;
    const canScrollRight =
      needsScroll && (hasOverflow ? scrollLeft + clientWidth < scrollWidth - 2 : true);
    setMetricScrollState((prev) =>
      prev.needsScroll !== needsScroll ||
      prev.canScrollLeft !== canScrollLeft ||
      prev.canScrollRight !== canScrollRight
        ? { needsScroll, canScrollLeft, canScrollRight }
        : prev,
    );
  }, []);

  const hasMetricContent = !isLoading && !error;
  useEffect(() => {
    if (!hasMetricContent) return;
    const container = metricContainerRef.current;
    const scrollEl = metricScrollRef.current;
    if (!container || !scrollEl) return;
    const check = () => requestAnimationFrame(updateMetricScrollState);
    const ro = new ResizeObserver(check);
    ro.observe(container);
    ro.observe(scrollEl);
    scrollEl.addEventListener("scroll", updateMetricScrollState);
    window.addEventListener("resize", check);
    const t = setTimeout(check, 100);
    return () => {
      clearTimeout(t);
      ro.disconnect();
      scrollEl.removeEventListener("scroll", updateMetricScrollState);
      window.removeEventListener("resize", check);
    };
  }, [updateMetricScrollState, hasMetricContent]);

  useLayoutEffect(() => {
    if (!hasMetricContent) return;
    const el = economicCircleRef.current;
    if (!el) return;

    const syncHeight = () => {
      if (!window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`).matches) {
        setCircleHeight(null);
        return;
      }
      const height = el.getBoundingClientRect().height;
      if (height <= 0) return;
      setCircleHeight((prev) => (prev !== null && Math.abs(prev - height) < 0.5 ? prev : height));
    };

    const ro = new ResizeObserver(() => requestAnimationFrame(syncHeight));
    ro.observe(el);
    window.addEventListener("resize", syncHeight);
    syncHeight();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncHeight);
    };
  }, [hasMetricContent]);

  const scrollMetrics = (direction) => {
    if (!metricScrollRef.current) return;
    metricScrollRef.current.scrollBy({
      left: direction * METRIC_SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };

  const handleCreateTransaction = async ({ amount, description }) => {
    if (!userInfo?.username) return;
    setIsLoadingCreateTransaction(true);
    try {
      const res = await physicalStoreService.createTransaction({
        amount,
        description,
        user: userInfo.username,
      });

      if (res.messageCode === 200000) {
        closeCreateTransactionModal();
        setTransactionObj(res.result);
        openQrCodeTransactionModal();
        return;
      }
      showErrorToast({ code: res.messageCode });
    } catch (err) {
      console.error("Error creating transaction:", err);
    } finally {
      setIsLoadingCreateTransaction(false);
    }
  };

  const handleOpenNewTransaction = async () => {
    if (!userInfo?.username) return;
    setIsCheckingVerification(true);
    try {
      const res = await physicalStoreService.verificationStatus(userInfo.username);

      if (
        res.messageCode === 500253 ||
        (res.messageCode === 200001 && res.result?.verificationStatus === "PENDING") ||
        (res.messageCode === 200001 && res.result?.verificationStatus === "REJECTED")
      ) {
        history.push("/dashboard-store/physical-store");
        return;
      }

      if (res.messageCode === 200001 && res.result?.verificationStatus === "APPROVED") {
        openCreateTransactionModal();
        return;
      }
    } catch (err) {
      console.error("Error checking verification:", err);
      showErrorToast({ code: 500000 });
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSubmitProof = async (formData) => {
    setIsLoadingSubmitProof(true);
    try {
      const res = await physicalStoreService.submitProof({
        ...formData,
        username: userInfo.username,
      });
      if (res.messageCode === 200000) {
        setVerificationIsPending(true);
        showSuccessToast({ detail: "physicalStoreProofSubmitted" });
      } else {
        showErrorToast({ code: res.messageCode });
      }
    } catch (err) {
      console.error("Error submitting proof:", err);
    } finally {
      setIsLoadingSubmitProof(false);
    }
  };

  const handleCloseQrCodeTransactionModal = () => {
    setTransactionObj(undefined);
    closeQrCodeTransactionModal();
  };

  useEffect(() => {
    const verifyIfTransactionIsPaid = async () => {
      if (!isOpenQrCodeTransactionModal || !transactionObj?.id) return;
      try {
        const res = await physicalStoreService.getPayment(transactionObj.id);
        if (res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          return;
        }
        if (res.result?.dateTimePayment) {
          setTransactionObj(res.result);
          closeQrCodeTransactionModal();
          setTransactionObj(undefined);
          showSuccessToast({ detail: "transactionDone" });
        }
      } catch (err) {
        console.error("Error verifying payment:", err);
      }
    };
    const interval = setInterval(verifyIfTransactionIsPaid, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenQrCodeTransactionModal, transactionObj]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <p className="text-red-500">{localeText("managerDashboardErrorLoad")}</p>
        </div>
      </DashboardLayout>
    );
  }

  const billingTotal = data.walletInfo?.balance || 0;
  const billingProcessing = data.walletInfo?.income || 0;

  const pendingStoreTransactionsCount = data.pendingStoreTransactionsCount ?? 0;
  const storeStatus =
    pendingStoreTransactionsCount > 0 ? localeText("managerStoreStatusPending") : null;
  const storeOrdersLabel =
    pendingStoreTransactionsCount === 1 ? localeText("orderSingular") : localeText("orderPlural");
  const storeSubtitle =
    pendingStoreTransactionsCount > 0
      ? localeText("awaitingProcessing") || "Aguardando processamento"
      : localeText("noPending") || "Nenhuma pendente";

  const networkEarnings = data?.lifetimeBonusInfo?.balance || 0;
  const networkLevel = localeText("managerNetworkLevelSenior");

  const dailyIncome = data?.lifetimeBonusInfo?.income || 0;
  const walletBalance = data?.walletInfo?.balance || 0;
  const walletChange = `+ ${currency} ${formatPrice(data?.walletInfo?.balancesale || 0)} ${localeText("managerBalanceSales")}`;

  const extracreditBalance = data?.extracoinInfo?.balance || 0;
  const extrabonusBalance = data?.extrabonusInfo?.balance || 0;
  const extrabonusExternalAds = data?.extrabonusInfo?.balanceElegibleToExternalAd || 0;

  return (
    <DashboardLayout>
      <DashboardTopbar userInfo={userInfo} userTypeInfo={userTypeInfo} />

      <SubmitProofDialog
        visible={isOpenSubmitProofModal}
        onHide={closeSubmitProofModal}
        onSubmit={handleSubmitProof}
        loading={isLoadingSubmitProof}
        isPending={verificationIsPending}
        rejectionReason={verificationRejectionReason}
      />

      <CreateTransactionDialog
        visible={isOpenCreateTransactionModal}
        onHide={closeCreateTransactionModal}
        onSubmit={handleCreateTransaction}
        loading={isLoadingCreateTransaction}
        currency={currencyCode}
        title={localeText("createChargeWithQrCode")}
        subtitle={localeText("createChargeWithQrCodeDescription")}
      />

      <Dialog
        header={localeText("qrCodeOfTransaction")}
        visible={isOpenQrCodeTransactionModal}
        className=" m-2 transaction-dialog"
        onHide={handleCloseQrCodeTransactionModal}
        draggable={false}
      >
        {transactionObj?.id && (
          <>
            <div className="flex flex-column gap-2">
              <div className="card border-round-lg">
                <QRCodeCanvas value={`${transactionObj.id}`} size={200} />
              </div>
              <div className="flex flex-column gap-1">
                <div
                  className="text-xs font-medium uppercase"
                  style={{ color: "var(--theme-text-muted)" }}
                >
                  {localeText("value") || "VALOR"}
                </div>
                <div className="font-medium" style={{ color: "var(--theme-text)" }}>
                  {formatPrice(transactionObj.amountPaid)} Extracredit
                </div>
              </div>
              {!transactionObj?.dateTimePayment && (
                <div
                  className="text-sm font-normal py-2 px-3 border-round-md mt-2"
                  style={{ color: "var(--theme-text)", backgroundColor: "#EBF1FF" }}
                >
                  {localeText("awaitingPayment")}...
                </div>
              )}
              {transactionObj?.dateTimePayment && (
                <div className="flex flex-column gap-1">
                  <div
                    className="text-xs font-medium uppercase"
                    style={{ color: "var(--theme-text-muted)" }}
                  >
                    {localeText("paymentDate") || "DATA"}
                  </div>
                  <div className="font-medium" style={{ color: "var(--theme-text)" }}>
                    {formatDate(transactionObj?.dateTimePayment)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {transactionObj?.dateTimePayment && (
          <div
            className="text-sm font-normal py-2 px-3 border-round-md mt-2"
            style={{ color: "var(--theme-text)", backgroundColor: "#EBF1FF", textAlign: "center" }}
          >
            {localeText("paymentDone")}
          </div>
        )}
      </Dialog>

      <div className="flex align-items-center justify-content-between mb-4">
        <div
          className="text-lg font-medium hidden md:block"
          style={{ color: "var(--theme-secondary)" }}
        >
          {localeText("managerTrackStoreHere")}
        </div>
        <div className="flex align-items-center gap-2">
          <Button
            icon="pi pi-qrcode"
            className="p-no-outline border-none bg-transparent text-sm font-medium"
            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
            loading={isCheckingVerification}
            onClick={handleOpenNewTransaction}
          />

          {!JSON.parse(localStorage.getItem("__extraconomy_ismobile__") || "false") && (
            <Link to="/shop">
              <div className="flex align-items-center gap-2">
                <Button
                  icon="pi pi-external-link"
                  label={
                    localeText("toTheStore").charAt(0).toUpperCase() +
                    localeText("toTheStore").slice(1).toLowerCase()
                  }
                  style={{ color: "var(--theme-primary, #1DAF61)", padding: "10px 20px" }}
                  className="p-no-outline border-none bg-transparent text-sm font-medium"
                  iconPos="right"
                  pt={{
                    icon: { className: "text-xs font-bold" },
                  }}
                />
              </div>
            </Link>
          )}
        </div>
      </div>
      <div className="grid mb-4">
        <div className="col-12 md:col-4">
          <KPICard
            title={localeText("managerBilling")}
            value={billingTotal}
            subtitle={`+ ${currency} ${formatPrice(billingProcessing)} ${localeText("managerInProcessing")}`}
            icon="pi pi-receipt"
            currency={currency}
            customBgColor="var(--theme-primary-dark, #346958)"
            customTextColor="rgba(255,255,255,0.95)"
            customSubtitleColor="rgba(255,255,255,0.85)"
          />
        </div>
        <div className="col-12 md:col-4">
          <Link to="/dashboard-store-transactions" className="no-underline">
            <KPICard
              title={localeText("managerLiveStore")}
              value={pendingStoreTransactionsCount}
              valueLabel={storeOrdersLabel}
              subtitle={storeSubtitle}
              icon="pi pi-shopping-bag"
              status={storeStatus}
              customBgColor="var(--theme-background, #FFFFFF)"
              customTextColor="var(--theme-text, #171717)"
              customSubtitleColor="var(--theme-text-muted, #5C5C5C)"
            />
          </Link>
        </div>
        <div className="col-12 md:col-4">
          <KPICard
            title={localeText("managerNetworkEarnings")}
            value={networkEarnings}
            subtitle={`${localeText("managerNetworkLevelLabel")}: ${networkLevel}`}
            icon="pi pi-sitemap"
            currency={currency}
            customBgColor="var(--theme-background, #FFFFFF)"
            customTextColor="var(--theme-text, #171717)"
            customSubtitleColor="var(--theme-text-muted, #5C5C5C)"
          />
        </div>
      </div>

      <div
        ref={metricContainerRef}
        className="relative mb-4 overflow-hidden"
        style={{ width: "100%" }}
      >
        {metricScrollState.needsScroll && (
          <button
            type="button"
            onClick={() => scrollMetrics(-1)}
            className="metric-scroll-nav metric-scroll-nav-left flex align-items-center justify-content-center border-round"
            aria-label={localeText("previous") || "Anterior"}
          >
            <i className="pi pi-chevron-left text-sm" />
          </button>
        )}
        {metricScrollState.needsScroll && (
          <button
            type="button"
            onClick={() => scrollMetrics(1)}
            className="metric-scroll-nav metric-scroll-nav-right flex align-items-center justify-content-center border-round"
            aria-label={localeText("next") || "Próximo"}
          >
            <i className="pi pi-chevron-right text-sm" />
          </button>
        )}
        <div
          ref={metricScrollRef}
          className="metric-scroll-container"
          style={{
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollSnapType: "x mandatory",
          }}
        >
          <div
            className="flex"
            style={{
              gap: 0,
              minWidth: "1054px",
              scrollSnapType: "x mandatory",
            }}
          >
            <div className="flex-1" style={{ minWidth: "238px", scrollSnapAlign: "start" }}>
              <MetricCard
                title={localeText("dailyIncome")}
                value={dailyIncome}
                label={localeText("managerTotalEarnings")}
                currency={currency}
              />
            </div>
            <div
              style={{ width: "1px", background: "var(--theme-border, #e0e0e0)", margin: "0 1rem" }}
            />
            <div className="flex-1" style={{ minWidth: "238px", scrollSnapAlign: "start" }}>
              <MetricCard
                title={localeText("wallet")}
                value={walletBalance}
                change={walletChange}
                currency={currency}
              />
            </div>
            <div
              style={{ width: "1px", background: "var(--theme-border, #e0e0e0)", margin: "0 1rem" }}
            />
            <div className="flex-1" style={{ minWidth: "238px", scrollSnapAlign: "start" }}>
              <MetricCard
                title={localeText("extracredit")}
                value={extracreditBalance}
                label={localeText("managerB2bPurchasingPower")}
                currency={currency}
              />
            </div>
            <div
              style={{ width: "1px", background: "var(--theme-border, #e0e0e0)", margin: "0 1rem" }}
            />
            <div className="flex-1" style={{ minWidth: "238px", scrollSnapAlign: "start" }}>
              <MetricCard
                title={localeText("managerBonusAds")}
                value={extrabonusBalance}
                label={`${formatFloatToInteger(extrabonusExternalAds)} ${localeText("managerAdsForExternalAds")}`}
                isInteger={true}
                currency={currency}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="manager-association-slot">
        <AssociationConfimeaShare username={userInfo?.username} />
      </div>

      <div className="grid manager-chart-circle-row mb-3">
        <div className="col-12 lg:col-8">
          <div
            className="manager-performance-chart-slot"
            style={circleHeight ? { height: circleHeight } : undefined}
          >
            <PerformanceChart constrained={circleHeight != null} />
          </div>
        </div>
        <div className="col-12 lg:col-4">
          <div ref={economicCircleRef} className="manager-economic-circle-slot">
            <EconomicCircle
              directsInfo={data.directsInfo || []}
              allNodesInfo={data.allNodesInfo || []}
              currency={currency}
              username={userInfo?.username}
              userTypeInfo={userTypeInfo}
            />
          </div>
        </div>
      </div>

      <RecentOperations currencySymbol={currency} />
    </DashboardLayout>
  );
};

export default ManagerDashboardHome;
